import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, MessageCircle, Send, Plus, Trash2, Loader2, X, Sparkles, Pin, Star, EyeOff, Eye, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { handsPosts, handsPostCreate, handsPostUpvote, handsPostDelete, handsComments, handsComment,
  handsAdminPin, handsAdminFeature, handsAdminHide, handsAdminBanAuthor } from "@/lib/handsApi";

const CATS = ["All", "Savings", "Subs", "Debt", "Bills", "Budget", "Habit"];
const SORTS = [
  { id: "trending", label: "Trending" },
  { id: "newest", label: "Newest" },
  { id: "top", label: "Top" },
];

export default function HandsCommunity() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("trending");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await handsPosts({ sort, category: category === "All" ? undefined : category, limit: 30 });
      setPosts(r.posts || []);
      setViewerIsAdmin(!!r.viewer_is_admin);
    } catch { toast.error("Couldn't load community"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, sort]);

  const upvote = async (p) => {
    if (!user) { toast.error("Sign in to upvote"); return; }
    try {
      const r = await handsPostUpvote(p.id);
      setPosts((list) => list.map((x) => x.id === p.id ? { ...x, upvoted_by_me: r.upvoted, upvotes: x.upvotes + (r.upvoted ? 1 : -1) } : x));
    } catch { toast.error("Couldn't upvote"); }
  };
  const del = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    try { await handsPostDelete(p.id); setPosts((list) => list.filter((x) => x.id !== p.id)); }
    catch { toast.error("Couldn't delete"); }
  };

  const applyModPatch = (pid, patch) => setPosts((list) => list.map((x) => x.id === pid ? { ...x, ...patch } : x));

  const modPin = async (p) => {
    const next = !p.is_pinned;
    try { await handsAdminPin(p.id, next); applyModPatch(p.id, { is_pinned: next }); toast.success(next ? "Pinned" : "Unpinned"); }
    catch { toast.error("Couldn't update"); }
  };
  const modFeature = async (p) => {
    const next = !p.is_featured;
    try { await handsAdminFeature(p.id, next); applyModPatch(p.id, { is_featured: next }); toast.success(next ? "Featured" : "Unfeatured"); }
    catch { toast.error("Couldn't update"); }
  };
  const modHide = async (p) => {
    const next = !p.hidden;
    try { await handsAdminHide(p.id, next); applyModPatch(p.id, { hidden: next }); toast.success(next ? "Post hidden" : "Post visible again"); }
    catch { toast.error("Couldn't update"); }
  };
  const modBan = async (p) => {
    const reason = window.prompt(`Ban "${p.user_name}" and hide all their posts?\n\nReason (optional):`, "Community guidelines violation");
    if (reason === null) return;
    try { await handsAdminBanAuthor(p.id, reason || undefined); toast.success(`${p.user_name} banned`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't ban"); }
  };

  return (
    <div data-testid="hands-community" className="pb-6 space-y-4">
      <section className="rounded-2xl bg-gradient-to-br from-[#22C55E]/20 via-[#0F2118] to-[#0A1512] border border-[#22C55E]/30 p-5">
        <div className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3"><Sparkles className="w-3 h-3" fill="currentColor" /> Community</div>
        <div className="font-black text-2xl">Money tips from real people.</div>
        <div className="text-white/60 text-sm mt-1">Share what works. Upvote what's smart. Save more together.</div>
        <button data-testid="post-add-btn" onClick={() => setShowForm(true)} className="mt-3 inline-flex items-center gap-1 bg-[#22C55E] text-[#0A1512] font-black text-xs uppercase tracking-wide px-4 py-2 rounded-full"><Plus className="w-4 h-4" /> Post advice</button>
      </section>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {SORTS.map((s) => (
          <button key={s.id} data-testid={`post-sort-${s.id}`} onClick={() => setSort(s.id)}
            className={"whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold " + (sort === s.id ? "bg-[#22C55E] text-[#0A1512]" : "bg-white/5 border border-white/10 text-white/70 hover:border-[#22C55E]/50")}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CATS.map((c) => (
          <button key={c} data-testid={`post-cat-${c}`} onClick={() => setCategory(c)}
            className={"whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold " + (category === c ? "bg-white text-[#0A1512]" : "bg-white/5 border border-white/10 text-white/60 hover:border-white/30")}>
            {c}
          </button>
        ))}
      </div>

      {loading ? <div className="text-white/40 text-sm py-6 text-center">Loading…</div> :
       posts.length === 0 ? <div data-testid="posts-empty" className="text-white/50 text-sm py-8 text-center bg-[#0F2118] rounded-2xl border border-[#1D3A2E]">No posts yet. Be first.</div> :
       <ul className="space-y-2">
         {posts.map((p) => (
           <li key={p.id} data-testid={`post-${p.id}`}
               className={"rounded-2xl border p-4 " + (
                 p.hidden ? "bg-[#2A0F0F] border-[#F87171]/40 opacity-70"
                 : p.is_pinned ? "bg-[#0F2118] border-[#FFD700]/50 shadow-[0_0_20px_rgba(255,215,0,0.12)]"
                 : p.is_featured ? "bg-[#0F2118] border-[#22C55E]/50"
                 : "bg-[#0F2118] border-[#1D3A2E]")}>
             <div className="flex gap-3">
               <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-black text-white text-xs" style={{ background: p.avatar_color }}>{(p.user_name || "?")[0]?.toUpperCase()}</div>
               <div className="min-w-0 flex-1">
                 <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/50 mb-1">
                   <span className="font-bold text-white/80">{p.user_name}</span>
                   <span>·</span>
                   <span className="text-[#22C55E] font-bold uppercase tracking-widest">{p.category}</span>
                   <span>·</span>
                   <span>{(p.created_at || "").slice(0, 10)}</span>
                   {p.is_pinned && (
                     <span data-testid={`post-pinned-${p.id}`} className="inline-flex items-center gap-0.5 bg-[#FFD700]/15 text-[#FFD700] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                       <Pin className="w-2.5 h-2.5" fill="currentColor" /> Pinned
                     </span>
                   )}
                   {p.is_featured && (
                     <span data-testid={`post-featured-${p.id}`} className="inline-flex items-center gap-0.5 bg-[#22C55E]/15 text-[#22C55E] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                       <Star className="w-2.5 h-2.5" fill="currentColor" /> Featured
                     </span>
                   )}
                   {viewerIsAdmin && p.hidden && (
                     <span data-testid={`post-hidden-${p.id}`} className="inline-flex items-center gap-0.5 bg-[#F87171]/15 text-[#F87171] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                       <EyeOff className="w-2.5 h-2.5" /> Hidden
                     </span>
                   )}
                   {user && p.user_id === user.id && (
                     <button data-testid={`post-del-${p.id}`} onClick={() => del(p)} className="ml-auto text-white/40 hover:text-[#F87171]"><Trash2 className="w-3 h-3" /></button>
                   )}
                 </div>
                 <div className="font-black text-white leading-snug">{p.title}</div>
                 <div className="text-sm text-white/70 mt-1 whitespace-pre-wrap">{p.body}</div>
                 <div className="mt-3 flex items-center gap-3 flex-wrap">
                   <button data-testid={`post-up-${p.id}`} onClick={() => upvote(p)}
                     className={"inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold " + (p.upvoted_by_me ? "bg-[#22C55E] text-[#0A1512]" : "bg-white/5 text-white/70 hover:bg-white/10")}>
                     <ChevronUp className="w-3.5 h-3.5" /> {p.upvotes}
                   </button>
                   <button data-testid={`post-cmt-${p.id}`} onClick={() => setOpenComments(openComments === p.id ? null : p.id)}
                     className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-white/5 text-white/70 hover:bg-white/10">
                     <MessageCircle className="w-3.5 h-3.5" /> {p.comments_count}
                   </button>
                   {viewerIsAdmin && (
                     <div className="ml-auto flex items-center gap-1 flex-wrap" data-testid={`post-mod-${p.id}`}>
                       <button data-testid={`mod-pin-${p.id}`} onClick={() => modPin(p)} title={p.is_pinned ? "Unpin" : "Pin"}
                         className={"inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-colors " + (p.is_pinned ? "bg-[#FFD700] text-[#0A1512] border-[#FFD700]" : "bg-white/5 border-white/10 text-white/70 hover:border-[#FFD700]/50")}>
                         <Pin className="w-3 h-3" fill={p.is_pinned ? "currentColor" : "none"} /> {p.is_pinned ? "Pinned" : "Pin"}
                       </button>
                       <button data-testid={`mod-feature-${p.id}`} onClick={() => modFeature(p)} title={p.is_featured ? "Unfeature" : "Feature"}
                         className={"inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-colors " + (p.is_featured ? "bg-[#22C55E] text-[#0A1512] border-[#22C55E]" : "bg-white/5 border-white/10 text-white/70 hover:border-[#22C55E]/50")}>
                         <Star className="w-3 h-3" fill={p.is_featured ? "currentColor" : "none"} /> {p.is_featured ? "Featured" : "Feature"}
                       </button>
                       <button data-testid={`mod-hide-${p.id}`} onClick={() => modHide(p)} title={p.hidden ? "Unhide" : "Hide"}
                         className={"inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-colors " + (p.hidden ? "bg-[#F87171] text-[#0A1512] border-[#F87171]" : "bg-white/5 border-white/10 text-white/70 hover:border-[#F87171]/50")}>
                         {p.hidden ? <><Eye className="w-3 h-3" /> Unhide</> : <><EyeOff className="w-3 h-3" /> Hide</>}
                       </button>
                       <button data-testid={`mod-ban-${p.id}`} onClick={() => modBan(p)} title="Ban author"
                         className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-[#F87171]/30 bg-[#F87171]/10 text-[#F87171] hover:bg-[#F87171]/20">
                         <ShieldAlert className="w-3 h-3" /> Ban
                       </button>
                     </div>
                   )}
                 </div>
                 {openComments === p.id && <CommentThread postId={p.id} onPosted={() => setPosts((list) => list.map((x) => x.id === p.id ? { ...x, comments_count: x.comments_count + 1 } : x))} />}
               </div>
             </div>
           </li>
         ))}
       </ul>}

      {showForm && <PostForm onClose={() => setShowForm(false)} onDone={(post) => { setShowForm(false); setPosts((list) => [post, ...list]); }} />}
    </div>
  );
}

function CommentThread({ postId, onPosted }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { handsComments(postId).then((r) => setItems(r.comments || [])).catch(() => {}); }, [postId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try { const r = await handsComment(postId, body); setItems((l) => [...l, r.comment]); setBody(""); onPosted?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't comment"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid={`thread-${postId}`}>
      {items.map((c) => (
        <div key={c.id} data-testid={`cmt-${c.id}`} className="flex gap-2 text-sm">
          <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center font-black text-white text-xs" style={{ background: c.avatar_color }}>{(c.user_name || "?")[0]?.toUpperCase()}</div>
          <div className="flex-1">
            <span className="font-bold text-white/90">{c.user_name}</span>
            <span className="text-white/70 ml-1">{c.body}</span>
          </div>
        </div>
      ))}
      <form onSubmit={submit} className="flex gap-2 mt-2">
        <input data-testid="cmt-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" className="flex-1 px-3 py-2 bg-[#0A1512] border border-[#1D3A2E] rounded-lg text-sm" />
        <button data-testid="cmt-submit" type="submit" disabled={busy || !body.trim()} className="bg-[#22C55E] text-[#0A1512] font-bold text-xs px-3 rounded-lg disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        </button>
      </form>
    </div>
  );
}

function PostForm({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", body: "", category: "Savings" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await handsPostCreate(f); toast.success("Posted"); onDone?.(r.post); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't post"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="post-form" className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-[#0F2118] rounded-t-3xl sm:rounded-2xl p-5 space-y-3 border border-[#1D3A2E]">
        <div className="flex items-center"><div className="font-black text-lg">Share advice</div><button type="button" onClick={onClose} className="ml-auto p-1 text-white/50 hover:text-white"><X className="w-4 h-4" /></button></div>
        <input required data-testid="post-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={140} placeholder="What worked for you?" className="w-full px-3 py-2.5 bg-[#0A1512] border border-[#1D3A2E] rounded-lg" />
        <select data-testid="post-cat" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full px-3 py-2.5 bg-[#0A1512] border border-[#1D3A2E] rounded-lg">
          {CATS.filter((c) => c !== "All").map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <textarea required data-testid="post-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={5} minLength={8} maxLength={6000} placeholder="Tell your story — specifics work best." className="w-full px-3 py-2.5 bg-[#0A1512] border border-[#1D3A2E] rounded-lg" />
        <button data-testid="post-submit" type="submit" disabled={busy} className="w-full bg-[#22C55E] text-[#0A1512] font-black py-3 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Post advice"}
        </button>
      </form>
    </div>
  );
}
